import { useState, useEffect, useRef } from 'react';
import {
  Monitor,
  Shield,
  Loader2,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle2,
  LogOut,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDeviceRegistration } from '@/hooks/useDeviceRegistration';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

/**
 * Full-screen overlay that blocks cashiers/employees from using an unregistered device.
 * Shows different states:
 * - Request approval form (if no pending request)
 * - Pending approval status (if waiting for admin)
 * - Device deactivated message (if device was disabled)
 */
export function UnregisteredDeviceOverlay() {
  const { profile, signOut } = useAuth();
  const { locations } = useLocations();
  const {
    deviceId,
    deviceType: _deviceType,
    isElectron,
    isLoading,
    isRegistered,
    isActive,
    requiresApproval: _requiresApproval,
    hasPendingRequest,
    pendingRequest,
    requestApproval,
    isCashier,
    wasDenied,
    deniedRequest,
  } = useDeviceRegistration();

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [deviceName, setDeviceName] = useState('');
  const [autoLogoutCountdown, setAutoLogoutCountdown] = useState<number | null>(null);
  const [isResending, setIsResending] = useState(false);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Resend notification to admins for existing pending request
  const handleResendRequest = async () => {
    if (!pendingRequest || !profile) return;

    setIsResending(true);
    try {
      const requesterName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'A user';
      const deviceNameToUse = pendingRequest.requested_device_name || 'a device';

      // Create a new notification for admins
      await supabase.from('notifications').insert({
        type: 'device_approval_request',
        title: 'Device Approval Reminder',
        message: `${requesterName} is still waiting for approval to register ${deviceNameToUse}.`,
        location_id: pendingRequest.requested_location_id,
        data: {
          request_id: pendingRequest.id,
          device_id: pendingRequest.device_id,
          device_name: deviceNameToUse,
          requester_id: profile.id,
          requester_name: requesterName,
          is_reminder: true,
        },
      });

      toast.success('Reminder sent', {
        description: 'A reminder has been sent to administrators.',
      });
    } catch (error: any) {
      toast.error('Failed to send reminder', {
        description: error.message || 'Please try again.',
      });
    } finally {
      setIsResending(false);
    }
  };

  // Auto-logout when request is denied
  useEffect(() => {
    if (wasDenied && deniedRequest) {
      // Start 10 second countdown
      setAutoLogoutCountdown(10);

      const countdownInterval = setInterval(() => {
        setAutoLogoutCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto sign out after 10 seconds
      logoutTimerRef.current = setTimeout(() => {
        signOut();
      }, 10000);

      return () => {
        clearInterval(countdownInterval);
        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current);
        }
      };
    }
  }, [wasDenied, deniedRequest, signOut]);

  // Don't show for admins/managers - they can self-register
  // Don't show if device is registered and active
  // Don't show while loading
  if (isLoading || !isCashier || (isRegistered && isActive)) {
    return null;
  }

  // Request was denied - show denial message and auto-logout
  if (wasDenied && deniedRequest) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-md">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0 pattern-dots" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-md px-6">
          <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center mb-8">
            <XCircle className="h-12 w-12 text-red-500" />
          </div>

          <h1 className="text-3xl font-display font-bold mb-2">Request Denied</h1>
          <p className="text-lg text-muted-foreground mb-2">
            Your device registration request was denied by an administrator.
          </p>
          {deniedRequest.review_notes && (
            <p className="text-sm text-muted-foreground mb-4 italic">
              "{deniedRequest.review_notes}"
            </p>
          )}

          <div className="bg-red-500/10 rounded-xl border border-red-500/20 p-4 w-full mb-6">
            <p className="text-sm text-red-600">
              You will be automatically signed out in{' '}
              <span className="font-bold">{autoLogoutCountdown ?? 0}</span> seconds.
            </p>
          </div>

          <Button
            variant="destructive"
            className="gap-2"
            onClick={() => signOut()}
          >
            <LogOut className="h-4 w-4" />
            Sign Out Now
          </Button>
        </div>

        <BrandingFooter />
      </div>
    );
  }

  // Device is registered but deactivated
  if (isRegistered && !isActive) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-md">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0 pattern-dots" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-md px-6">
          <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center mb-8">
            <AlertTriangle className="h-12 w-12 text-red-500" />
          </div>

          <h1 className="text-3xl font-display font-bold mb-2">Device Deactivated</h1>
          <p className="text-lg text-muted-foreground mb-8">
            This device has been deactivated by an administrator. Please contact your manager for assistance.
          </p>

          <div className="bg-muted/50 rounded-xl border p-4 w-full">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Device ID:</span>{' '}
              <code className="text-xs">{deviceId?.slice(0, 20)}...</code>
            </p>
          </div>

          <Button
            variant="outline"
            className="mt-6 gap-2"
            onClick={() => signOut()}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>

        <BrandingFooter />
      </div>
    );
  }

  // Pending approval request
  if (hasPendingRequest && pendingRequest) {
    const requestTime = new Date(pendingRequest.created_at);
    const expiresAt = new Date(pendingRequest.expires_at);

    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-md">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0 pattern-dots" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-md px-6">
          <div className="relative mb-8">
            <div className="w-24 h-24 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Clock className="h-12 w-12 text-amber-500" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
              <Loader2 className="h-4 w-4 text-white animate-spin" />
            </div>
          </div>

          <h1 className="text-3xl font-display font-bold mb-2">Awaiting Approval</h1>
          <p className="text-lg text-muted-foreground mb-2">
            Your device registration request is pending administrator approval.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Please ask a manager or administrator to approve your device.
          </p>

          <div className="bg-card rounded-2xl border p-6 w-full space-y-4">
            <div className="flex items-center gap-3 text-left">
              <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm text-muted-foreground">Requested Location</p>
                <p className="font-medium">{pendingRequest.location?.name || 'Unknown'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-left">
              <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm text-muted-foreground">Requested At</p>
                <p className="font-medium">
                  {requestTime.toLocaleTimeString()} on {requestTime.toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Request expires at {expiresAt.toLocaleTimeString()} on {expiresAt.toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              variant="blaze"
              className="gap-2"
              onClick={handleResendRequest}
              disabled={isResending}
            >
              {isResending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Send Reminder
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => signOut()}
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>

        <BrandingFooter />
      </div>
    );
  }

  // Request approval form
  const handleRequestApproval = async () => {
    if (!selectedLocationId) {
      toast.error('Select a location', {
        description: 'Please select the location where this device will be used.',
      });
      return;
    }

    try {
      await requestApproval.mutateAsync({
        locationId: selectedLocationId,
        deviceName: deviceName || undefined,
      });
      toast.success('Request submitted', {
        description: 'Your device registration request has been submitted. Please wait for administrator approval.',
      });
    } catch (error: any) {
      toast.error('Failed to submit request', {
        description: error.message || 'Please try again or contact your administrator.',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-md">
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0 pattern-dots" />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center max-w-md px-6">
        <div className="w-24 h-24 rounded-full bg-blue-500/10 flex items-center justify-center mb-8">
          <Monitor className="h-12 w-12 text-blue-500" />
        </div>

        <h1 className="text-3xl font-display font-bold mb-2">Device Not Registered</h1>
        <p className="text-lg text-muted-foreground mb-2">
          {profile?.first_name ? `Hey ${profile.first_name}, ` : ''}
          this device needs to be registered before you can start working.
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          Select your location and request administrator approval.
        </p>

        <div className="bg-card rounded-2xl border p-6 w-full space-y-4">
          <div className="space-y-2 text-left">
            <Label htmlFor="location">Work Location</Label>
            <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <SelectTrigger id="location">
                <SelectValue placeholder="Select your location" />
              </SelectTrigger>
              <SelectContent className="z-[200]">
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 text-left">
            <Label htmlFor="deviceName">Device Name (optional)</Label>
            <Input
              id="deviceName"
              placeholder={isElectron ? 'POS Terminal 1' : 'Front Counter iPad'}
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Give this device a name to help identify it later.
            </p>
          </div>

          <Button
            variant="blaze"
            size="lg"
            className="w-full gap-2"
            onClick={handleRequestApproval}
            disabled={requestApproval.isPending || !selectedLocationId}
          >
            {requestApproval.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" />
                Request Approval
              </>
            )}
          </Button>
        </div>

        <div className="mt-6 bg-muted/50 rounded-xl border p-4 w-full">
          <div className="flex items-start gap-3 text-left">
            <CheckCircle2 className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Device Type: {isElectron ? 'Desktop Terminal' : 'Web Browser'}</p>
              <p className="text-xs text-muted-foreground mt-1">
                ID: <code>{deviceId?.slice(0, 24)}...</code>
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          className="mt-6 gap-2"
          onClick={() => signOut()}
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>
      </div>

      <BrandingFooter />
    </div>
  );
}

function BrandingFooter() {
  return (
    <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2">
      <div className="flex items-center gap-2 text-muted-foreground/60">
        <div className="w-6 h-6 rounded-lg flex items-center justify-center overflow-hidden">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="w-6 h-6 object-contain" />
        </div>
        <span className="text-sm font-display font-semibold">BlazePOS</span>
      </div>
      <a
        href="https://blazepos.app"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors"
      >
        blazepos.app
      </a>
    </div>
  );
}
